import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  View,
  Text,
  ScrollView,
  Pressable,
  useWindowDimensions,
  Platform,
  TouchableOpacity,
  Alert,
  ActivityIndicator,
  Modal,
  TextInput,
  KeyboardAvoidingView,
} from 'react-native';
import Svg, { Path, Circle } from 'react-native-svg';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { useTranslation } from 'react-i18next';
import PTLanguageToggle from '../PerfectTruck/PTLanguageToggle';
import { AdminColors } from '../Admin/constants/adminColors';
import { styles as baseStyles } from './SubmitHaulbackScreen.styles';
import {
  fetchHaulbackScreenData,
  fetchHaulbacks,
  submitHaulback,
  submitBulkHaulback,
  parseError,
  SubmissionReasonCode,
  OrderInfo,
  HarborImageUploadResult,
} from '../../api/deliveryRpmApi';
import { getLocalTimestamp } from '../../api/apiClient';
import { RootState } from '../../store';
import HarborPhotoUpload from './components/HarborPhotoUpload';
import NotifySupervisorModal from './NotifySupervisorModal';
import { useDarkStyles, useDarkColor } from '../../theme/useDarkStyles';

// ─── Types ────────────────────────────────────────────────────────────────────

interface StagedSsccParam {
  sscc: string;
  orderInfo: {
    customer: string;
    sscc: string;
    localRoute: string;
    date: string;
    caseQuantity: number;
    salesRep: string;
    picker: string;
  };
}

interface SubmitHaulbackScreenProps {
  onNavigate: (screen: string, params?: Record<string, any>) => void;
  /** Called after a successful submission so the landing page can refresh. */
  onSubmitSuccess?: (newRecord: Record<string, any>) => void;
  route?: {
    params?: {
      sscc?: string;
      proceedWhenNotScheduled?: boolean;
      /** When present, this is a multi-SSCC submission. */
      additionalSsccs?: StagedSsccParam[];
      /** When present, this is a customer-based (no SSCC) submission. */
      customer?: { cof: string; name: string; sscc?: string; ssccList?: string[] };
    };
  };
}

type RescheduleStatus = 'Rescheduled' | 'Return To Stock';

// Default labels when pictureTitle / textFieldTitle is blank
const DEFAULT_PHOTO_LABEL = 'Photo';
const DEFAULT_TEXT_LABEL = 'Additional Details';

const EQUIPMENT_BREAKDOWN_OPTIONS = ['EPJ', 'Truck', 'Liftgate'] as const;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const todayIso = () => new Date().toISOString().slice(0, 10);

const formatDate = (iso: string) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${m}/${d}/${y}`;
};

const UNICODE_EMOJI_REGEX = /\p{Extended_Pictographic}/u;

const hasUnicodeEmoji = (value: string) => UNICODE_EMOJI_REGEX.test(value);

// ─── Icons ────────────────────────────────────────────────────────────────────

const CheckIcon = () => (
  <Svg width={12} height={12} viewBox="0 0 24 24" fill="none">
    <Path d="M5 12l5 5L20 7" stroke="#fff" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

const WarnIcon = () => (
  <Svg width={20} height={20} viewBox="0 0 24 24" fill="none">
    <Path d="M12 3L2.5 20h19L12 3Z" stroke="#E5A408" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    <Path d="M12 9.5v4.5" stroke="#E5A408" strokeWidth={2} strokeLinecap="round" />
    <Circle cx={12} cy={17} r={0.4} fill="#E5A408" stroke="#E5A408" strokeWidth={2} />
  </Svg>
);

const ReasonCodeIcon: React.FC<{ iconName?: string }> = ({ iconName }) => {
  const styles = useDarkStyles(baseStyles);
  if (!iconName) {
    return <MaterialIcons name="help-outline" size={18} color={AdminColors.gray450} />;
  }
  if (hasUnicodeEmoji(iconName)) {
    return <Text style={styles.rcEmojiIcon}>{iconName}</Text>;
  }
  return <MaterialIcons name={iconName as any} size={18} color={AdminColors.blue700} />;
};

// ─── Main screen ──────────────────────────────────────────────────────────────

const SubmitHaulbackScreen: React.FC<SubmitHaulbackScreenProps> = ({
  onNavigate,
  onSubmitSuccess,
  route,
}) => {
  const dc = useDarkColor();
  const styles = useDarkStyles(baseStyles);
  const { t, i18n } = useTranslation('harbor');
  const selectedLocation = useSelector((state: RootState) => state.appData?.selectedLocation);
  const params = route?.params ?? {};
  const sscc = params.sscc ?? '';
  const proceedWhenNotScheduled = params.proceedWhenNotScheduled ?? false;
  /** When present, multi-SSCC submission mode */
  const additionalSsccs: StagedSsccParam[] = params.additionalSsccs ?? [];
  const isMultiSscc = additionalSsccs.length > 1;
  /** When present, customer-based submission — the customer's SSCC is used to load screen data */
  const customerParam = params.customer;
  const customerSsccList = customerParam?.ssccList?.filter(Boolean) ?? [];
  /** The effective SSCC: explicit param takes priority, then the customer's first SSCC. */
  const effectiveSscc = sscc || customerParam?.sscc || customerSsccList[0] || '';
  const customerSsccCount = customerSsccList.length;
  const isCustomerBulkSscc = customerSsccCount > 1;
  const shouldUseBulkSubmit = isMultiSscc || isCustomerBulkSscc;
  const bulkSsccList = isCustomerBulkSscc ? customerSsccList : additionalSsccs.map(s => s.sscc);

  // Modal to show the full list of SSCCs in multi-submit mode
  const [ssccListModalVisible, setSsccListModalVisible] = useState(false);

  const { width: viewportWidth } = useWindowDimensions();
  const isMobile = viewportWidth < 768;

  const reasonTileColumns = useMemo(() => {
    if (viewportWidth < 768) return 2;
    if (viewportWidth < 1280) return 4;
    return 6;
  }, [viewportWidth]);

  const reasonTileWidth = useMemo(() => {
    const pageHorizontalPadding = 48;
    const cardHorizontalPadding = 48;
    const gridGap = 12;
    const availableWidth = Math.max(viewportWidth - pageHorizontalPadding - cardHorizontalPadding, 280);
    return Math.floor((availableWidth - gridGap * (reasonTileColumns - 1)) / reasonTileColumns);
  }, [viewportWidth, reasonTileColumns]);

  // ── Load screen data ─────────────────────────────────────────────────────
  const [orderInfo, setOrderInfo] = useState<OrderInfo | null>(null);
  const [reasonCodes, setReasonCodes] = useState<SubmissionReasonCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const data = await fetchHaulbackScreenData(effectiveSscc, proceedWhenNotScheduled, undefined, i18n.language);
      setOrderInfo(data.orderInfo);
      setReasonCodes(data.reasonCodes);
    } catch (err) {
      setLoadError(parseError(err));
    } finally {
      setLoading(false);
    }
  }, [effectiveSscc, proceedWhenNotScheduled, i18n.language]);

  // Store selected reason code ID (language-independent) to restore across language changes
  const savedReasonIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (effectiveSscc) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [effectiveSscc, loadData , i18n.language]);

  // ── Reason code selection ────────────────────────────────────────────────
  const [selectedReasonId, setSelectedReasonId] = useState<number | null>(null);
  const [reasonError, setReasonError] = useState('');

  const selectedReason = useMemo(
    () => reasonCodes.find(r => r.id === selectedReasonId) ?? null,
    [reasonCodes, selectedReasonId],
  );

  // When a reason is selected, save its ID for restoration after language change
  useEffect(() => {
    if (selectedReasonId !== null) {
      savedReasonIdRef.current = selectedReasonId;
    }
  }, [selectedReasonId]);

  // When reasonCodes updates (e.g., from language change), restore previous selection by ID.
  // IDs stay constant across languages even though the displayed label is translated.
  useEffect(() => {
    if (savedReasonIdRef.current !== null && reasonCodes.length > 0) {
      const restored = reasonCodes.find(r => r.id === savedReasonIdRef.current);
      if (restored && restored.id !== selectedReasonId) {
        setSelectedReasonId(restored.id);
      }
    }
  }, [reasonCodes]);

  // Identify the "Equipment Breakdown" reason by its stable ID rather than its translated label —
  // `reasonCode` is localized per `i18n.language`, so a hardcoded English string match would fail
  // once the UI is switched to Spanish/French. We resolve the ID once via an English-language
  // lookup against the same config endpoint (id is language-independent).
  const [equipmentBreakdownReasonId, setEquipmentBreakdownReasonId] = useState<number | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetchHaulbacks({ isActive: 'true', limit: '200', language: 'en' })
      .then(res => {
        if (cancelled) return;
        const found = res.items.find(
          r => r.reasonCode?.trim().toLowerCase() === 'equipment breakdown',
        );
        if (found) setEquipmentBreakdownReasonId(found.id);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const handleReasonSelect = (id: number) => {
    setSelectedReasonId(id);
    setReasonError('');
  };

  // "Equipment Breakdown" reason uses its own translated card labels instead of the
  // backend-provided pictureTitle/textFieldTitle, which are not localized per language.
  const isEquipmentBreakdownReason = selectedReasonId !== null && selectedReasonId === equipmentBreakdownReasonId;

  // ── Derived display config from selected reason ───────────────────────────
  const showPhoto = !!(selectedReason?.pictureRequired || selectedReason?.pictureOptional);
  const photoRequired = !!selectedReason?.pictureRequired;
  const photoLabel = isEquipmentBreakdownReason
    ? t('haulback_submit.label_broken_item_image')
    : selectedReason?.pictureTitle?.trim() || t('haulback_submit.section_photo_attachment');

  const showTextField = !!(selectedReason?.textFieldRequired || selectedReason?.textFieldOptional);
  const textFieldRequired = !!selectedReason?.textFieldRequired;
  const textFieldLabel = isEquipmentBreakdownReason
    ? t('haulback_submit.label_broken_item_detail')
    : selectedReason?.textFieldTitle?.trim() || t('haulback_submit.section_details');

  // ── Reschedule selection ─────────────────────────────────────────────────
  const [rescheduleStatus, setRescheduleStatus] = useState<RescheduleStatus | null>(null);
  const [rescheduleError, setRescheduleError] = useState('');

  const handleRescheduleSelect = (val: RescheduleStatus) => {
    setRescheduleStatus(val);
    setRescheduleError('');
  };

  // ── Pallet selection ─────────────────────────────────────────────────────
  const [palletSelectionType, setPalletSelectionType] = useState<'Full' | 'Partial' | null>(null);
  const [palletOverrideQuantity, setPalletOverrideQuantity] = useState('');
  const [palletOverrideError, setPalletOverrideError] = useState('');

  const handlePalletTypeSelect = (type: 'Full' | 'Partial') => {
    setPalletSelectionType(type);
    if (type === 'Full') {
      setPalletOverrideQuantity('');
      setPalletOverrideError('');
    }
  };

  const handlePalletOverrideChange = (value: string) => {
    // Remove any non-numeric characters
    const numericOnly = value.replace(/[^0-9]/g, '');
    // Limit to 4 digits
    const truncated = numericOnly.slice(0, 4);
    setPalletOverrideQuantity(truncated);
    // Clear error on input change
    if (truncated.length > 0) {
      setPalletOverrideError('');
    }
  };

  const validatePalletOverride = (): boolean => {
    if (palletSelectionType !== 'Partial') return true;

    const overrideNum = parseInt(palletOverrideQuantity, 10);
    const originalQuantity = isMultiSscc
      ? additionalSsccs.reduce((sum, s) => sum + (s.orderInfo.caseQuantity ?? 0), 0)
      : orderInfo?.caseQuantity ?? 0;

    if (isNaN(overrideNum) || overrideNum <= 0 || overrideNum >= originalQuantity) {
      setPalletOverrideError(t('haulback_submit.error_pallet_override_invalid'));
      return false;
    }

    setPalletOverrideError('');
    return true;
  };

  // ── Additional details (comments) ───────────────────────────────────────
  const [additionalDetails, setAdditionalDetails] = useState('');

  // ── Equipment breakdown (shown when reasonCodeCategory === 'Equipment') ─
  const [equipmentBreakdown, setEquipmentBreakdown] = useState('');
  const [equipmentError, setEquipmentError] = useState('');
  // also show when reasonCodeCategory is missing but reason name matches
  const showMissingEquipment = isEquipmentBreakdownReason;
  const EQUIPMENT_BREAKDOWN_MAX = 100;

  // Equipment breakdown options with i18n support (keys based on language, not static English values)
  const equipmentBreakdownOptions = useMemo(() => [
    t('haulback_submit.equipment_breakdown_epj'),
    t('haulback_submit.equipment_breakdown_truck'),
    t('haulback_submit.equipment_breakdown_liftgate'),
  ], [t]);

  const handleEquipmentChipToggle = (opt: string) => {
    setEquipmentBreakdown(prev => (prev === opt ? '' : opt));
    setEquipmentError('');
  };

  // Reset additional details when the reason code changes
  const handleReasonSelectWithReset = (id: number) => {
    handleReasonSelect(id);
    setAdditionalDetails('');
    setEquipmentBreakdown('');
    setEquipmentError('');
  };

  // ── Picture attachment ───────────────────────────────────────────────────
  const [pictureAttached, setPictureAttached] = useState(false);
  const [uploadResult, setUploadResult] = useState<HarborImageUploadResult | null>(null);

  const handlePhotoUploaded = (result: HarborImageUploadResult) => {
    setUploadResult(result);
    setPictureAttached(true);
  };

  // ── Submit enabled guard ─────────────────────────────────────────────────
  const pictureOk = !photoRequired || pictureAttached;
  const textFieldOk = !textFieldRequired || additionalDetails.trim().length > 0;
  const equipmentOk = !showMissingEquipment || equipmentBreakdown.trim().length > 0;
  const palletSelectionOk = !isMultiSscc && !isCustomerBulkSscc && !!palletSelectionType;
  const palletOverrideOk = palletSelectionType === 'Full' || (palletSelectionType === 'Partial' && palletOverrideQuantity.trim().length > 0 && !palletOverrideError);
  const canSubmit = !!selectedReasonId && !!rescheduleStatus && pictureOk && textFieldOk && equipmentOk && (shouldUseBulkSubmit || (palletSelectionOk && palletOverrideOk));

  // ── Submission ───────────────────────────────────────────────────────────
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // ── Dirty state for cancel guard ──────────────────────────────────────────
  const isDirty = selectedReasonId !== null || rescheduleStatus !== null || additionalDetails.length > 0 || pictureAttached || equipmentBreakdown.length > 0 || palletSelectionType !== null;
  const [cancelConfirmVisible, setCancelConfirmVisible] = useState(false);

  // ── Supervisor warning modal state ────────────────────────────────────────
  const [supervisorWarningVisible, setSupervisorWarningVisible] = useState(false);
  const [supervisorConfirmVisible, setSupervisorConfirmVisible] = useState(false);

  // ── Supervisor automatic notification modal state ─────────────────────────
  const [supervisorNotifyVisible, setSupervisorNotifyVisible] = useState(false);
  // Set once the haulback is saved, so retrying a failed send does not create a duplicate
  const savedHaulbackIdRef = useRef<number | null>(null);

  const handleSubmit = async () => {
    let valid = true;
    if (!selectedReasonId) {
      setReasonError(t('haulback_submit.error_select_reason'));
      valid = false;
    }
    if (!rescheduleStatus) {
      setRescheduleError(t('haulback_submit.error_select_reschedule'));
      valid = false;
    }
    if (photoRequired && !pictureAttached) {
      Alert.alert(t('haulback_submit.alert_photo_title'), t('haulback_submit.alert_photo_required'));
      valid = false;
    }
    if (showMissingEquipment && !equipmentBreakdown.trim()) {
      setEquipmentError(t('haulback_submit.error_equipment_required'));
      valid = false;
    }
    // NEW: Pallet selection validation (only for single SSCC)
    if (!shouldUseBulkSubmit) {
      if (!palletSelectionType) {
        setPalletOverrideError(t('haulback_submit.error_select_pallet_type'));
        valid = false;
      } else if (!validatePalletOverride()) {
        valid = false;
      }
    }
    if (!valid) return;

    // Supervisor warning flow (step 1) takes precedence
    if (selectedReason?.supervisorNotificationWarning) {
      setSupervisorWarningVisible(true);
      return;
    }

    // Proceeds to submit; automatic notification opens the modal first and saves on Send
    await doSubmit();
  };

  // ── Core submit logic ─────────────────────────────────────────────────────
  // Build the imageResponse JSON string: { [originalFileName]: imageUrl }
  const buildImageResponse = () => uploadResult
    ? JSON.stringify({
        [uploadResult.originalFileName]: uploadResult.imageUrl,
      })
    : undefined;

  // Saves the single-SSCC haulback once; a retry after a failed notification reuses the saved ID
  const saveSingleHaulback = async (): Promise<number> => {
    if (savedHaulbackIdRef.current !== null) return savedHaulbackIdRef.current;
    const response = await submitHaulback({
      sscc: effectiveSscc,
      reasonCodeId: selectedReasonId!,
      rescheduleStatus: rescheduleStatus!,
      palletType: palletSelectionType === 'Full' ? 'FULL' : 'PARTIAL',
      caseOverrideQuantity: palletSelectionType === 'Partial' ? parseInt(palletOverrideQuantity, 10) : undefined,
      overrideNotScheduled: proceedWhenNotScheduled || undefined,
      equipmentBreakdown: equipmentBreakdown.trim() || undefined,
      imageResponse: buildImageResponse(),
      comments: additionalDetails.trim() || undefined,
      location: selectedLocation?.name || '',
      customerCofId: customerParam?.cof || undefined,
      clientTimestamp: getLocalTimestamp(),
      clientTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
    savedHaulbackIdRef.current = response.haulbackId;

    if (onSubmitSuccess) {
      const today = todayIso();
      onSubmitSuccess({
        id: String(response.haulbackId),
        customer: response.orderInfo?.customer ?? orderInfo?.customer ?? '',
        cof: '',
        sscc: effectiveSscc,
        localRoute: response.orderInfo?.localRoute ?? orderInfo?.localRoute ?? '',
        date: response.orderInfo?.date ?? today,
        status: (response.rescheduleStatus ?? rescheduleStatus) === 'Return To Stock'
          ? 'Return to Stock'
          : 'Reschedule',
        reasonCode: selectedReason?.reasonCode ?? '',
        caseQuantity: response.orderInfo?.caseQuantity ?? orderInfo?.caseQuantity ?? 0,
        driver: response.driver ?? '',
        salesRep: response.orderInfo?.salesRep ?? orderInfo?.salesRep ?? '',
        picker: response.orderInfo?.picker ?? orderInfo?.picker ?? '',
      });
    }
    return response.haulbackId;
  };

  const doSubmit = async () => {
    setSubmitting(true);
    setSubmitError('');
    try {
      if (shouldUseBulkSubmit) {
        await submitBulkHaulback({
          ssccList: bulkSsccList,
          reasonCodeId: selectedReasonId!,
          rescheduleStatus: rescheduleStatus!,
          palletType: 'FULL',
          overrideNotScheduled: proceedWhenNotScheduled || undefined,
          equipmentBreakdown: equipmentBreakdown.trim() || undefined,
          imageResponse: buildImageResponse(),
          comments: additionalDetails.trim() || undefined,
          location: selectedLocation?.name || '',
          clientTimestamp: getLocalTimestamp(),
          clientTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
        // For bulk submission, no notification modal needed
        onNavigate('Delivery RPM', { toastMessage: t('haulback_submit.success_message') });
      } else if (selectedReason?.supervisorAutomaticNotification) {
        // The haulback is saved only when Send is pressed in the notification modal
        setSupervisorNotifyVisible(true);
      } else {
        await saveSingleHaulback();
        onNavigate('Delivery RPM', { toastMessage: t('haulback_submit.success_message') });
      }
    } catch (err) {
      const message = parseError(err);
      setSubmitError(message || t('haulback_submit.error_submit_generic'));
    } finally {
      setSubmitting(false);
    }
  };

  // ── Supervisor warning modal handlers ────────────────────────────────────
  const handleSupervisorWarningYes = () => {
    setSupervisorWarningVisible(false);
    doSubmit();
  };

  const handleSupervisorWarningNo = () => {
    setSupervisorWarningVisible(false);
    setSupervisorConfirmVisible(true);
  };

  const handleSupervisorConfirmProceed = () => {
    setSupervisorConfirmVisible(false);
    doSubmit();
  };

  const handleSupervisorConfirmBack = () => {
    setSupervisorConfirmVisible(false);
  };

  // Notification sent: the haulback was saved on Send, so navigate away with the success toast
  const handleSupervisorNotifyClose = () => {
    setSupervisorNotifyVisible(false);
    onNavigate('Delivery RPM', { toastMessage: t('haulback_submit.success_message') });
  };

  // Cancel: nothing is saved unless a send attempt already saved it, in which case leave to avoid a duplicate
  const handleSupervisorNotifyBack = () => {
    setSupervisorNotifyVisible(false);
    if (savedHaulbackIdRef.current !== null) {
      onNavigate('Delivery RPM');
    }
  };

  const handleCancel = () => {
    if (isDirty) {
      setCancelConfirmVisible(true);
    } else {
      onNavigate('Delivery RPM');
    }
  };

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: AdminColors.bgPage }}>
        <ActivityIndicator size="large" color={AdminColors.blue700} />
      </View>
    );
  }

  // ── Load error ────────────────────────────────────────────────────────────
  if (loadError) {
    return (
      <View style={styles.errorBanner}>
        <MaterialIcons name="error-outline" size={20} color={AdminColors.red500} />
        <Text style={styles.errorBannerText}>{loadError}</Text>
        <TouchableOpacity onPress={() => onNavigate('Delivery RPM')}>
          <Text style={styles.returnLink}>{t('haulback_submit.action_return')}</Text>
        </TouchableOpacity>
      </View>
    );
  }

  // ── No order info ─────────────────────────────────────────────────────────
  if (!orderInfo) {
    return (
      <View style={styles.errorBanner}>
        <MaterialIcons name="error-outline" size={20} color={AdminColors.red500} />
        <Text style={styles.errorBannerText}>
          {t('haulback_submit.error_order_unavailable')}
        </Text>
        <TouchableOpacity onPress={() => onNavigate('Delivery RPM')}>
          <Text style={styles.returnLink}>{t('haulback_submit.action_return')}</Text>
        </TouchableOpacity>
      </View>
    );
  }

  // ── Main render ───────────────────────────────────────────────────────────

  return (
    <KeyboardAvoidingView style={styles.root} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
      {/* ── Cancel confirmation dialog ────────────────────────────────────────── */}
      <Modal
        visible={cancelConfirmVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setCancelConfirmVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalCard}>
            <Text style={styles.modalCardTitle}>{t('haulback_submit.modal_unsaved_title')}</Text>
            <Text style={styles.modalCardBody}>
              {t('haulback_submit.modal_unsaved_body')}
            </Text>
            <View style={styles.modalActions}>
              <TouchableOpacity
                style={styles.modalSecondaryBtn}
                onPress={() => setCancelConfirmVisible(false)}
              >
                <Text style={styles.modalSecondaryBtnText}>{t('haulback_submit.modal_action_stay')}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.modalBtn}
                onPress={() => {
                  setCancelConfirmVisible(false);
                  onNavigate('Delivery RPM');
                }}
              >
                <Text style={styles.modalBtnText}>{t('haulback_submit.modal_action_leave')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* ── Supervisor warning modal (step 1): "Have you contacted your DDL?" ── */}
      <Modal
        visible={supervisorWarningVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setSupervisorWarningVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalCard}>
            <Text style={styles.modalCardTitle}>{t('haulback_submit.supervisor_warning_modal_title')}</Text>
            <Text style={styles.modalCardBody}>{t('haulback_submit.supervisor_warning_modal_body')}</Text>
            <View style={styles.modalActions}>
              <TouchableOpacity style={styles.modalSecondaryBtn} onPress={handleSupervisorWarningNo}>
                <Text style={styles.modalSecondaryBtnText}>{t('haulback_submit.supervisor_warning_modal_no')}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.modalBtn} onPress={handleSupervisorWarningYes}>
                <Text style={styles.modalBtnText}>{t('haulback_submit.supervisor_warning_modal_yes')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* ── Supervisor warning modal (step 2): "Are you sure?" ── */}
      <Modal
        visible={supervisorConfirmVisible}
        transparent
        animationType="fade"
        onRequestClose={handleSupervisorConfirmBack}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalCard}>
            <Text style={styles.modalCardTitle}>{t('haulback_submit.supervisor_warning_confirm_title')}</Text>
            <Text style={styles.modalCardBody}>{t('haulback_submit.supervisor_warning_confirm_body')}</Text>
            <View style={styles.modalActions}>
              <TouchableOpacity style={styles.modalSecondaryBtn} onPress={handleSupervisorConfirmBack}>
                <Text style={styles.modalSecondaryBtnText}>{t('haulback_submit.supervisor_warning_confirm_back')}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.modalBtn} onPress={handleSupervisorConfirmProceed}>
                <Text style={styles.modalBtnText}>{t('haulback_submit.supervisor_warning_confirm_proceed')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* ── Supervisor automatic notification modal ───────────────────────────── */}
      {supervisorNotifyVisible && (
        <NotifySupervisorModal
          recordType="Haulback"
          createRecord={saveSingleHaulback}
          onClose={handleSupervisorNotifyClose}
          onBack={handleSupervisorNotifyBack}
        />
      )}

      {/* ── SSCC List modal (multi-submit / customer multi-SSCC) ───────────── */}
      <Modal
        visible={ssccListModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setSsccListModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={[styles.modalCard, { maxWidth: 420, width: '100%' }]}>
            <Text style={styles.modalCardTitle}>
              {customerParam && customerSsccCount > 1
                ? t('haulback_submit.customer_sscc_list_modal_title')
                : t('haulback_submit.sscc_list_modal_title')}
            </Text>
            {(customerParam && customerSsccCount > 1 ? customerSsccList : additionalSsccs.map(item => item.sscc)).map((ssccValue, idx, list) => (
              <View key={`${ssccValue}-${idx}`} style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 6, borderBottomWidth: idx < list.length - 1 ? 1 : 0, borderBottomColor: dc('#F0F0F0', 'border') }}>
                <Text style={{ fontSize: 13, color: dc('#838383'), fontFamily: 'System', minWidth: 20 }}>{idx + 1}.</Text>
                <Text style={{ fontSize: 14, color: dc('#1A1A1A'), fontFamily: 'System', letterSpacing: 0.5, flex: 1 }}>{ssccValue}</Text>
              </View>
            ))}
            <View style={[styles.modalActions, { marginTop: 12 }]}>
              <TouchableOpacity
                style={styles.modalBtn}
                onPress={() => setSsccListModalVisible(false)}
                accessibilityLabel={t('haulback_submit.sscc_list_modal_ok')}
              >
                <Text style={styles.modalBtnText}>{t('haulback_submit.sscc_list_modal_ok')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <ScrollView
        style={styles.root}
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
      >
        {/* ── Breadcrumb ─────────────────────────────────────────────────── */}
        <View style={styles.breadcrumb}>
          <TouchableOpacity onPress={() => onNavigate('Harbor')}>
            <Text style={styles.bcLink}>{t('breadcrumb_harbor')}</Text>
          </TouchableOpacity>
          <Text style={styles.bcSep}>{' › '}</Text>
          <TouchableOpacity onPress={() => onNavigate('Delivery RPM')}>
            <Text style={styles.bcLink}>{t('haulback_submit.breadcrumb_rpm')}</Text>
          </TouchableOpacity>
          <Text style={styles.bcSep}>{' › '}</Text>
          <Text style={styles.bcCurrent}>{t('haulback_submit.page_title')}</Text>
        </View>

        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Text style={styles.pageTitle}>{t('haulback_submit.page_title')}</Text>
          <PTLanguageToggle compact={isMobile} />
        </View>

        {/* ── Order info box (read-only) ─────────────────────────────────── */}
        <View style={styles.infoBox}>
          <View style={styles.roLabel}>
            <Text style={styles.roText}>{t('haulback_submit.section_order_info_readonly')}</Text>
          </View>
          <View style={styles.infoGrid}>
            <View style={styles.infoField}>
              <Text style={styles.infoKey}>{t('haulback_submit.label_customer')}</Text>
              <Text style={styles.infoVal}>{orderInfo.customer}</Text>
            </View>
            <View style={styles.infoField}>
              <Text style={styles.infoKey}>{t('haulback_submit.label_sscc')}</Text>
              {customerParam && customerSsccCount > 1 ? (
                <TouchableOpacity onPress={() => setSsccListModalVisible(true)} accessibilityLabel={`${customerSsccCount} SSCCs`}>
                  <Text style={[styles.infoVal, styles.mono, { color: dc('#034895'), textDecorationLine: 'underline' }]}>
                    {customerSsccCount} SSCCs
                  </Text>
                </TouchableOpacity>
              ) : isMultiSscc ? (
                <TouchableOpacity onPress={() => setSsccListModalVisible(true)} accessibilityLabel={t('haulback_submit.label_sscc_count', { count: additionalSsccs.length })}>
                  <Text style={[styles.infoVal, styles.mono, { color: dc('#034895'), textDecorationLine: 'underline' }]}>
                    {t('haulback_submit.label_sscc_count', { count: additionalSsccs.length })}
                  </Text>
                </TouchableOpacity>
              ) : (
                <Text style={[styles.infoVal, styles.mono]}>{orderInfo.sscc}</Text>
              )}
            </View>
            <View style={styles.infoField}>
              <Text style={styles.infoKey}>{t('haulback_submit.label_local_route')}</Text>
              <Text style={styles.infoVal}>{orderInfo.localRoute}</Text>
            </View>
            <View style={styles.infoField}>
              <Text style={styles.infoKey}>{t('haulback_submit.label_date')}</Text>
              <Text style={styles.infoVal}>{formatDate(orderInfo.date)}</Text>
            </View>
            <View style={styles.infoField}>
              <Text style={styles.infoKey}>{t('haulback_submit.label_case_quantity')}</Text>
              <Text style={styles.infoVal}>
                {isMultiSscc
                  ? additionalSsccs.reduce((sum, s) => sum + (s.orderInfo.caseQuantity ?? 0), 0)
                  : orderInfo.caseQuantity}
              </Text>
            </View>
          </View>
        </View>

        {/* ── Reason Code ───────────────────────────────────────────────── */}
        <View style={styles.card}>
          <Text style={styles.cardTitle}>{t('haulback_submit.section_reason')}</Text>
          <Text style={styles.cardSub}>
            {t('haulback_submit.section_reason_sub')}
          </Text>

          {reasonCodes.length === 0 ? (
            <Text style={{ fontSize: 14, color: AdminColors.gray350, paddingVertical: 8 }}>
              {t('common.no_data')}
            </Text>
          ) : (
            <View style={styles.rcGrid}>
              {reasonCodes.map(rc => {
                const isSel = selectedReasonId === rc.id;
                return (
                  <Pressable
                    key={rc.id}
                    style={[styles.rcTile, { width: reasonTileWidth }, isSel && styles.rcTileSel]}
                    onPress={() => handleReasonSelectWithReset(rc.id)}
                    accessibilityRole="radio"
                    accessibilityState={{ selected: isSel }}
                    accessibilityLabel={rc.reasonCode}
                  >
                    {isSel && (
                      <View style={styles.rcCheck}>
                        <CheckIcon />
                      </View>
                    )}
                    <View style={styles.rcReasonRow}>
                      <View style={styles.rcReasonIconWrap}>
                        <ReasonCodeIcon iconName={rc.iconName} />
                      </View>
                      <Text style={styles.rcLabel}>{rc.reasonCode}</Text>
                    </View>
                  </Pressable>
                );
              })}
            </View>
          )}

          {!!reasonError && <Text style={styles.errorText}>{reasonError}</Text>}
        </View>

        {/* ── Photo Attachment — shown only when picture toggle is on ────────── */}
        {/* key forces remount on reason change so internal preview state clears */}
        {showMissingEquipment && (
          <View style={styles.card}>
            <Text style={styles.cardTitle}>{t('haulback_submit.section_equipment_breakdown')}</Text>
            <Text style={styles.cardSub}>{t('haulback_submit.section_equipment_breakdown_sub')}</Text>
            {isEquipmentBreakdownReason ? (
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginTop: 8 }}>
                {equipmentBreakdownOptions.map(opt => {
                  const isSel = equipmentBreakdown === opt;
                  return (
                    <Pressable
                      key={opt}
                      onPress={() => handleEquipmentChipToggle(opt)}
                      style={{
                        paddingHorizontal: 18,
                        paddingVertical: 9,
                        borderRadius: 20,
                        borderWidth: 1.5,
                        borderColor: isSel ? AdminColors.blue700 : '#D0D0D0',
                        backgroundColor: isSel ? AdminColors.blue25 : '#fff',
                      }}
                      accessibilityRole="checkbox"
                      accessibilityState={{ checked: isSel }}
                      accessibilityLabel={opt}
                    >
                      <Text style={{ fontSize: 14, color: isSel ? AdminColors.blue700 : '#555', fontWeight: isSel ? '600' : '400' }}>
                        {opt}
                      </Text>
                    </Pressable>
                  );
                })}
              </View>
            ) : (
              <>
                <TextInput
                  style={styles.additionalDetailsInput}
                  value={equipmentBreakdown}
                  onChangeText={v => setEquipmentBreakdown(v.slice(0, EQUIPMENT_BREAKDOWN_MAX))}
                  placeholder={t('haulback_submit.placeholder_equipment_breakdown')}
                  placeholderTextColor={dc("#BDBDBD")}
                  maxLength={EQUIPMENT_BREAKDOWN_MAX}
                  accessibilityLabel={t('haulback_submit.section_equipment_breakdown')}
                />
                <Text style={{ fontSize: 12, color: AdminColors.gray350, textAlign: 'right', marginTop: 4 }}>
                  {equipmentBreakdown.length}/{EQUIPMENT_BREAKDOWN_MAX}
                </Text>
              </>
            )}
            {!!equipmentError && <Text style={styles.errorText}>{equipmentError}</Text>}
          </View>
        )}

        {/* ── Photo Attachment — shown only when picture toggle is on ────────── */}
        {showPhoto && (
          <View key={`photo-${selectedReasonId ?? 'none'}`} style={styles.card}>
            <Text style={styles.cardTitle}>
              {photoLabel}
              {photoRequired ? <Text style={styles.requiredAsterisk}> *</Text> : null}
            </Text>
            <Text style={styles.cardSub}>
              {photoRequired
                ? t('haulback_submit.section_photo_attachment_required')
                : t('haulback_submit.section_photo_attachment_optional')}
            </Text>
            <HarborPhotoUpload
              label={pictureAttached ? t('haulback_submit.label_photo_attached') : t('haulback_submit.label_attach_photo')}
              onUploaded={handlePhotoUploaded}
            />
          </View>
        )}

        {/* ── Additional Details — shown only when text field toggle is on ───── */}
        {showTextField && (
          <View style={styles.card}>
            <Text style={styles.cardTitle}>
              {textFieldLabel}
              {textFieldRequired
                ? <Text style={styles.requiredAsterisk}> *</Text>
                : null}
            </Text>
            <Text style={styles.cardSub}>
              {textFieldRequired
                ? t('haulback_submit.section_details_required_sub')
                : t('haulback_submit.section_details_optional_sub')}
            </Text>
            <TextInput
              style={[
                styles.additionalDetailsInput,
                textFieldRequired && additionalDetails.trim().length === 0 && styles.additionalDetailsInputEmpty,
              ]}
              value={additionalDetails}
              onChangeText={setAdditionalDetails}
              placeholder={
                textFieldRequired
                  ? t('haulback_submit.placeholder_details_required')
                  : t('haulback_submit.placeholder_details_optional')
              }
              placeholderTextColor={dc("#BDBDBD")}
              multiline
              numberOfLines={3}
              textAlignVertical="top"
              accessibilityLabel={textFieldLabel}
            />
            {textFieldRequired && additionalDetails.trim().length === 0 && (
              <Text style={styles.errorText}>{t('haulback_submit.error_details_required')}</Text>
            )}
          </View>
        )}

        {/* ── Reschedule ────────────────────────────────────────────────── */}
        <View style={styles.card}>
          <Text style={styles.cardTitle}>{t('haulback_submit.section_reschedule')}</Text>
          <Text style={styles.cardSub}>{t('haulback_submit.section_reschedule_sub')}</Text>
          <View style={styles.radioRow}>
            {(['Rescheduled', 'Return To Stock'] as RescheduleStatus[]).map(opt => {
              const isSel = rescheduleStatus === opt;
              const label = opt === 'Return To Stock'
                ? t('haulback_submit.status_return_to_stock')
                : t('haulback_submit.status_reschedule');
              return (
                <Pressable
                  key={opt}
                  style={[styles.radioOpt, isSel && styles.radioOptSel]}
                  onPress={() => handleRescheduleSelect(opt)}
                  accessibilityRole="radio"
                  accessibilityState={{ selected: isSel }}
                  accessibilityLabel={label}
                >
                  <View style={[styles.radioDot, isSel && styles.radioDotSel]}>
                    {isSel && <View style={styles.radioDotInner} />}
                  </View>
                  <Text style={styles.radioLabel}>{label}</Text>
                </Pressable>
              );
            })}
          </View>
          {!!rescheduleError && <Text style={styles.errorText}>{rescheduleError}</Text>}
        </View>

        {/* ── Pallet Type Selection ─────────────────────────────────────────── */}
        <View style={styles.card}>
          <Text style={styles.cardTitle}>{t('haulback_submit.section_pallet_type')}</Text>
          <Text style={styles.cardSub}>{t('haulback_submit.section_pallet_type_sub')}</Text>
          
          {shouldUseBulkSubmit ? (
            // Multi-SSCC: Show red banner with message and subtext, no selection controls
            <View style={styles.palletTypeMultiBanner}>
              <Text style={styles.palletTypeMultiBannerText}>
                {t('haulback_submit.pallet_type_multi_sscc_message')}
              </Text>
              <Text style={styles.palletTypeMultiBannerSubtext}>
                {t('haulback_submit.pallet_type_multi_sscc_subtext')}
              </Text>
            </View>
          ) : (
            // Single SSCC: Show selection options
            <>
              <View style={styles.palletTypeOptions}>
                {(['Full', 'Partial'] as const).map(opt => {
                  const isSel = palletSelectionType === opt;
                  const label = opt === 'Full'
                    ? t('haulback_submit.pallet_type_full')
                    : t('haulback_submit.pallet_type_partial');
                  return (
                    <Pressable
                      key={opt}
                      style={[styles.radioOpt, isSel && styles.radioOptSel]}
                      onPress={() => handlePalletTypeSelect(opt)}
                      accessibilityRole="radio"
                      accessibilityState={{ selected: isSel }}
                      accessibilityLabel={label}
                    >
                      <View style={[styles.radioDot, isSel && styles.radioDotSel]}>
                        {isSel && <View style={styles.radioDotInner} />}
                      </View>
                      <Text style={styles.radioLabel}>{label}</Text>
                    </Pressable>
                  );
                })}
              </View>
              
              {/* Case Override field - shown only when Partial is selected */}
              {palletSelectionType === 'Partial' && (
                <View style={styles.palletOverrideContainer}>
                  <Text style={[styles.cardTitle, { fontSize: 13, marginTop: 12, marginBottom: 4 }]}>
                    {t('haulback_submit.label_case_override')}
                    <Text style={styles.requiredAsterisk}> *</Text>
                  </Text>
                  
                  <TextInput
                    style={[
                      styles.palletOverrideInput,
                      !!palletOverrideError && styles.palletOverrideInputError,
                    ]}
                    value={palletOverrideQuantity}
                    onChangeText={handlePalletOverrideChange}
                    placeholder={t('haulback_submit.placeholder_case_override')}
                    placeholderTextColor={dc("#BDBDBD")}
                    keyboardType="numeric"
                    maxLength={4}
                    accessibilityLabel={t('haulback_submit.label_case_override')}
                  />
                  
                  {!!palletOverrideError && (
                    <Text style={[styles.errorText, styles.palletOverrideErrorText]}>
                      {palletOverrideError}
                    </Text>
                  )}
                </View>
              )}
            </>
          )}
        </View>

        {!!submitError && (
          <View style={styles.warnBanner}>
            <WarnIcon />
            <Text style={styles.warnBannerText}>{submitError}</Text>
          </View>
        )}

        {/* ── Form actions ──────────────────────────────────────────────── */}
        <View style={styles.formActions}>
          <TouchableOpacity
            style={styles.cancelBtn}
            onPress={handleCancel}
            disabled={submitting}
            accessibilityRole="button"
            accessibilityLabel={t('common.cancel')}
          >
            <Text style={styles.cancelBtnText}>{t('common.cancel')}</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.submitBtn, (!canSubmit || submitting) && styles.submitBtnDisabled]}
            onPress={handleSubmit}
            disabled={!canSubmit || submitting}
            accessibilityRole="button"
            accessibilityLabel={t('haulback_submit.button_submit')}
            accessibilityState={{ disabled: !canSubmit || submitting }}
          >
            {submitting ? (
              <ActivityIndicator size="small" color="#fff" />
            ) : (
              <Text style={[styles.submitBtnText, (!canSubmit) && styles.submitBtnTextDisabled]}>
                {t('haulback_submit.button_submit')}
              </Text>
            )}
          </TouchableOpacity>
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
};

export default SubmitHaulbackScreen;
